import { useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, HelpCircle, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useReviewDescWidth, REVIEW_DESC_CSS_VAR } from '@/hooks/useReviewDescWidth';
import { SalesOrderEditableSpecCell } from './SalesOrderEditableSpecCell';
import { SalesOrderLineItemInfoPopover } from './SalesOrderLineItemInfoPopover';
import { isSpecValidForRematch, REMATCH_INVALID_SPEC_MESSAGE } from '../utils/lineItem';
import type { SalesOrderLineItemDto } from '../types/salesOrder.types';

// Compact, review-focused editable line-items list — a deliberate counterpart to
// SalesOrderLineItemsTable (which carries row checkboxes, sorting, pricing
// columns, and a bulk-action bar that are noise during review). Mirrors the
// Quotation module's ReviewLineItemsList: a lean row with the six spec cells,
// inline diffs against the AI's original extraction, a status icon, and a revert
// popover. Edits commit through the same onChange contract as the table.

const DIAMETER_OPTIONS = [6, 8, 10, 12, 16, 20, 25, 28, 32, 36, 40, 50].map((d) => ({
  value: String(d),
  label: `${d} mm`,
}));
const LENGTH_OPTIONS = [2, 3, 4, 5, 6, 7.5, 9, 10.5, 12, 13.5, 15, 18].map((l) => ({ value: String(l), label: `${l} m` }));
// G33 excluded — not a sellable grade (auto-promoted to G40; no base markup). 86d3j5qca.
const GRADE_OPTIONS = ['40', '60', '75', '80'].map((g) => ({ value: g, label: `G${g}` }));
const WELDABLE_OPTIONS = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
];

interface StatusVisual {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}

const STATUS_VISUALS: Record<string, StatusVisual> = {
  MATCHED: { label: 'Matched', icon: CheckCircle2, color: 'text-success' },
  NEEDS_CLARIFICATION: { label: 'Needs info', icon: AlertTriangle, color: 'text-warning' },
  NO_MATCH: { label: 'No match', icon: XCircle, color: 'text-destructive' },
  SUMMARY_ONLY: { label: 'Summary', icon: HelpCircle, color: 'text-muted-foreground' },
  manual: { label: 'Manual', icon: HelpCircle, color: 'text-muted-foreground' },
};

const STATUS_COUNT_ORDER = ['MATCHED', 'NEEDS_CLARIFICATION', 'NO_MATCH', 'SUMMARY_ONLY'];

// 86d3fcbte — shared column geometry so the header row and the data rows stay
// pixel-aligned. The list is a horizontally-scrollable "table": the row number +
// description are pinned left (sticky) while the spec columns scroll. Spec-grid
// track widths fit the edited value + its ~28px SalesOrderEditableSpecCell overhead
// (px-1.5 padding + gap-1 + the always-reserved chevron) AND the inline original-
// extraction hint shown *beside* an edited cell — so neither wraps. Description gets
// a fixed width (truncate + tooltip as a last resort) so the scroll columns line up
// across rows. Kept identical to the Quotation review list.
const REVIEW_SPEC_GRID = 'grid grid-cols-[96px_100px_84px_52px_92px_48px] gap-x-1';
const REVIEW_NUM_COL = 'w-8 shrink-0';
const REVIEW_STATUS_COL = 'w-14 shrink-0';
const REVIEW_ACTION_COL = 'w-7 shrink-0';

function MatchStatusIcon({ status }: { status: string | null }) {
  const v = (status ? STATUS_VISUALS[status] : undefined) ?? STATUS_VISUALS.SUMMARY_ONLY;
  const Icon = v.icon;
  return (
    <span title={v.label} className="inline-flex shrink-0 items-center">
      <Icon className={cn('h-3.5 w-3.5', v.color)} aria-label={v.label} />
    </span>
  );
}

// Small muted subtext beneath an edited cell showing the AI's original value.
function OriginalHint({ children }: { children: ReactNode }) {
  return (
    <span
      className="shrink-0 text-[9px] leading-none text-muted-foreground/70"
      title="Original extraction"
    >
      {children}
    </span>
  );
}

interface ParsedSpec {
  diameterMm: number | null;
  lengthM: number | null;
  gradeCode: string | null;
  isWeldable: boolean;
  quantity: number | null;
  unit: string;
}

function parseOriginalSpec(json: string | null): ParsedSpec | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as ParsedSpec;
  } catch {
    return null;
  }
}

// Rematch requires a spec valid per the server validator (valid grade + positive
// diameter/length), not mere presence — else "Check all matches" fires requests
// that 400 on invalid lines (86d3dxnd7).
function canCheckMatch(li: SalesOrderLineItemDto): boolean {
  return isSpecValidForRematch(li);
}

interface Props {
  lineItems: SalesOrderLineItemDto[];
  readOnly?: boolean;
  onChange: (next: SalesOrderLineItemDto[]) => void;
  onRematchRow?: (clientId: string) => void;
  onRevertRow?: (clientId: string) => void;
}

export function SalesOrderReviewLineItems({
  lineItems,
  readOnly = false,
  onChange,
  onRematchRow,
  onRevertRow,
}: Props) {
  const [isCheckingAll, setIsCheckingAll] = useState(false);
  const { width: descWidth, containerRef, startResize: startDescResize } = useReviewDescWidth();

  function update(clientId: string, patch: Partial<SalesOrderLineItemDto>) {
    onChange(
      lineItems.map((li) => (li.clientId === clientId ? { ...li, ...patch, isEdited: true } : li)),
    );
  }

  const statusCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const li of lineItems) {
      if (!li.matchStatus) continue;
      counts.set(li.matchStatus, (counts.get(li.matchStatus) ?? 0) + 1);
    }
    return STATUS_COUNT_ORDER.filter((s) => counts.has(s)).map((s) => ({
      status: s,
      count: counts.get(s)!,
    }));
  }, [lineItems]);

  const matchableCount = useMemo(() => lineItems.filter(canCheckMatch).length, [lineItems]);

  function handleCheckAllMatches() {
    if (!onRematchRow) return;
    setIsCheckingAll(true);
    try {
      lineItems.filter(canCheckMatch).forEach((li) => onRematchRow(li.clientId));
    } finally {
      // onRematchRow is fire-and-forget; clear the transient pressed state next tick.
      setIsCheckingAll(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="sticky top-0 z-10 border-b bg-background/95 px-3 py-2 backdrop-blur">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">
            Line Items <span className="text-muted-foreground">({lineItems.length})</span>
          </h2>
          {!readOnly && onRematchRow && matchableCount > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCheckAllMatches}
              disabled={isCheckingAll}
              className="h-6 gap-1.5 px-2 text-[11px]"
              title={`Run match against NetSuite for ${matchableCount} line item${matchableCount === 1 ? '' : 's'} with complete specs`}
            >
              {isCheckingAll ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              Check matches
            </Button>
          )}
        </div>
        {statusCounts.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-2">
            {statusCounts.map(({ status, count }) => (
              <div key={status} className="flex items-center gap-1">
                <MatchStatusIcon status={status} />
                <span className="text-[10px] tabular-nums text-muted-foreground">
                  {STATUS_VISUALS[status]?.label ?? status} ×{count}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 86d3fcbte — horizontally + vertically scrollable region. Rows are sized
          to content (w-max) so the spec columns can overflow and scroll; the
          row #/description group is pinned left (sticky) and the header <li> is
          pinned top, so the header corner stays put in both axes. */}
      <ol
        ref={containerRef}
        className="flex-1 divide-y overflow-auto"
        style={{ [REVIEW_DESC_CSS_VAR]: `${descWidth}px` } as React.CSSProperties}
      >
        {/* Sticky column header as the first <li> so it shares the scroll region
            (and thus the scrollbar-adjusted width) with the data rows. Mirrors
            the row's pinned-left + scrolling-right two-group layout exactly. */}
        <li className="sticky top-0 z-20 flex w-max min-w-full bg-muted text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {/* position:sticky also provides the containing block for the absolute
              resize handle below — no separate `relative` needed. */}
          <div className="sticky left-0 z-10 flex items-center gap-1.5 border-r bg-muted py-1 pl-3 pr-2">
            <span className={cn(REVIEW_NUM_COL, 'text-right')}>#</span>
            <span className="shrink-0" style={{ width: `var(${REVIEW_DESC_CSS_VAR})` }}>
              Description
            </span>
            {/* Drag handle — resize the pinned description column (persisted). */}
            <div
              role="separator"
              aria-orientation="vertical"
              onPointerDown={startDescResize}
              title="Drag to resize the description column"
              className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize touch-none hover:bg-primary/40 active:bg-primary"
            />
          </div>
          <div className="flex items-center gap-1.5 py-1 pl-2 pr-3">
            <div className={cn(REVIEW_SPEC_GRID, 'shrink-0')}>
              <span className="px-1.5">Ø</span>
              <span className="px-1.5">Length</span>
              <span className="px-1.5">Grade</span>
              <span className="px-1.5">W</span>
              <span className="px-1.5 text-right">Qty</span>
              <span className="px-1.5">Unit</span>
            </div>
            <span className={cn(REVIEW_STATUS_COL, 'text-center')}>Status</span>
            <span className={REVIEW_ACTION_COL} aria-hidden />
          </div>
        </li>
        {lineItems.length === 0 && (
          <li className="px-3 py-6 text-center text-xs text-muted-foreground">No line items.</li>
        )}
        {lineItems.map((li, idx) => {
          const sku = li.matchedSalesDescription ?? li.rawDescription ?? '—';
          const isMatched = !!li.matchedSalesDescription;
          const o = parseOriginalSpec(li.originalSpecJson);
          const showEditedMarker = li.isEdited && !!o;

          const diffDiameter = o && o.diameterMm !== li.diameterMm;
          const diffLength = o && o.lengthM !== li.lengthM;
          const diffGrade = o && o.gradeCode !== li.gradeCode;
          const diffWeldable = o && o.isWeldable !== li.isWeldable;
          const diffQuantity = o && o.quantity !== li.quantity;

          return (
            <li
              key={li.clientId}
              className="flex w-max min-w-full text-[11px]"
              title={showEditedMarker ? 'Edited from extraction' : undefined}
            >
              {/* Pinned-left group: row # + full description stay visible while the
                  spec columns scroll horizontally. The edited marker rides here so
                  it's never scrolled out of view. */}
              <div
                className={cn(
                  'sticky left-0 z-10 flex items-center gap-1.5 border-r bg-background py-1 pl-3 pr-2',
                  // pl-2.5 (10px) + border-l-2 (2px) = pl-3 (12px), so the edit
                  // marker doesn't shift the row out of alignment with the others.
                  showEditedMarker && 'border-l-2 border-l-warning pl-2.5',
                )}
              >
                <span
                  className={cn(
                    REVIEW_NUM_COL,
                    'text-right font-mono tabular-nums text-muted-foreground',
                  )}
                >
                  #{idx + 1}
                </span>
                <p
                  className={cn(
                    'shrink-0 truncate text-xs',
                    isMatched ? 'font-medium text-foreground' : 'italic text-muted-foreground',
                  )}
                  style={{ width: `var(${REVIEW_DESC_CSS_VAR})` }}
                  title={sku}
                >
                  {sku}
                </p>
              </div>
              {/* Scrolling group: spec cells (fixed-track grid so columns align
                  across rows) + status + action. */}
              <div className="flex items-center gap-1.5 py-1 pl-2 pr-3">
                <div className={cn(REVIEW_SPEC_GRID, 'shrink-0 items-center text-muted-foreground')}>
                <div className="flex items-center gap-1">
                  <SalesOrderEditableSpecCell
                    kind="select"
                    value={li.diameterMm != null ? String(li.diameterMm) : null}
                    options={DIAMETER_OPTIONS}
                    disabled={readOnly}
                    ariaLabel="Diameter (mm)"
                    className="py-0 text-[11px] whitespace-nowrap w-auto min-w-0 flex-1"
                    onCommit={(v) =>
                      update(li.clientId, { diameterMm: v != null ? Number(v) : null })
                    }
                  />
                  {diffDiameter && (
                    <OriginalHint>{o!.diameterMm != null ? `${o!.diameterMm} mm` : '—'}</OriginalHint>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <SalesOrderEditableSpecCell
                    kind="select"
                    value={li.lengthM != null ? String(li.lengthM) : null}
                    options={LENGTH_OPTIONS}
                    disabled={readOnly}
                    ariaLabel="Length (m)"
                    className="py-0 text-[11px] whitespace-nowrap w-auto min-w-0 flex-1"
                    onCommit={(v) => update(li.clientId, { lengthM: v != null ? Number(v) : null })}
                  />
                  {diffLength && (
                    <OriginalHint>{o!.lengthM != null ? `${o!.lengthM} m` : '—'}</OriginalHint>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <SalesOrderEditableSpecCell
                    kind="select"
                    value={li.gradeCode}
                    options={GRADE_OPTIONS}
                    disabled={readOnly}
                    ariaLabel="Grade"
                    className="py-0 text-[11px] whitespace-nowrap w-auto min-w-0 flex-1"
                    onCommit={(v) => update(li.clientId, { gradeCode: v })}
                  />
                  {diffGrade && (
                    <OriginalHint>{o!.gradeCode != null ? `G${o!.gradeCode}` : '—'}</OriginalHint>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <SalesOrderEditableSpecCell
                    kind="select"
                    value={li.isWeldable ? 'Y' : 'N'}
                    options={WELDABLE_OPTIONS}
                    disabled={readOnly}
                    ariaLabel="Weldable"
                    className="py-0 text-[11px] whitespace-nowrap w-auto min-w-0 flex-1"
                    onCommit={(v) => update(li.clientId, { isWeldable: v === 'Y' })}
                  />
                  {diffWeldable && <OriginalHint>{o!.isWeldable ? 'Y' : 'N'}</OriginalHint>}
                </div>
                <div className="flex items-center gap-1">
                  <SalesOrderEditableSpecCell
                    kind="number"
                    value={li.quantity ?? null}
                    min={0}
                    disabled={readOnly}
                    ariaLabel="Quantity"
                    className="py-0 text-[11px] whitespace-nowrap w-auto min-w-0 flex-1"
                    onCommit={(v) => update(li.clientId, { quantity: v })}
                  />
                  {diffQuantity && (
                    <OriginalHint>{o!.quantity != null ? `${o!.quantity}` : '—'}</OriginalHint>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {/* Unit fixed to "pcs"; the only unit, read-only (86d3dxnd7). */}
                  <div className="px-1.5 py-0 text-[11px] text-foreground">pcs</div>
                </div>
              </div>
                <div className={cn(REVIEW_STATUS_COL, 'flex justify-center')}>
                  {li.matchStatus && <MatchStatusIcon status={li.matchStatus} />}
                </div>
                <div className={cn(REVIEW_ACTION_COL, 'flex justify-center')}>
                  <SalesOrderLineItemInfoPopover
                    item={li}
                    onRevert={onRevertRow ? () => onRevertRow(li.clientId) : undefined}
                    onRematch={onRematchRow ? () => onRematchRow(li.clientId) : undefined}
                    rematchDisabledReason={
                      isSpecValidForRematch(li) ? undefined : REMATCH_INVALID_SPEC_MESSAGE
                    }
                  />
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
